import { useEffect, useRef, useState } from "react";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToGuidesWrapper,
  ContentWrapper,
  LegislationDetailContainer,
  NoDataContainer,
  SvgContainer,
  TitleWrapper,
} from "./NewsletterStyled";

// LIBRARIES
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

//ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// MISC
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { DownloadFileType } from "components/Atoms/DownloadFileAtom/DownloadFileAtomModel";
import { appRoutes } from "config/AppRoutesConfig";
import { COLORS } from "constants/variables";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import DownloadFileAtom from "components/Atoms/DownloadFileAtom/DownloadFileAtom";
import Pagination from "components/Atoms/Paginations/Pagination";
import HeaderTab from "components/HeaderTab/HeaderTab";

// HOOKS
import { uploadBaseUrl } from "services/base";
import newsAndMediaService from "services/newsAndMediaService";

const Newsletter = () => {
  // PROPERTIES

  // API REQUESTS

  // LIBRARY CONSTANTS
  const navigate = useNavigate();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const { t } = useTranslation();
  const topRef = useRef(null);
  // STATE CONSTANTS
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [brochures, setBrochures] = useState<{ data: any[] }>({ data: [] });
  const [currentPage, setCurrentPage] = useState<number>(1);
  //eslint-disable-next-line
  const [totalPages, setTotalPages] = useState<number>(10);
  const pageSize = 6;
  const paginatedData = brochures?.data?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    topRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const content = (
    <ContentWrapper>
      {paginatedData?.map((file) => (
        <DownloadFileAtom
          key={file?.id}
          type={DownloadFileType.Orders}
          title={file?.attributes?.title}
          institution={file?.attributes?.dir}
          date={file?.attributes?.date}
          pdfUrl={`${uploadBaseUrl}${file?.attributes?.document_id?.data?.attributes?.attached_document?.data?.attributes?.url}`}
        />
      ))}
    </ContentWrapper>
  );

  const tabs = [
    {
      id: 0,
      tabTitle: t("view.newsletter.tabs.DGPC"),
      content: content,
      totalPages: totalPages,
      currentPage: currentPage,
      handlePageChange: handlePageChange,
    },
    // {
    //   id: 1,
    //   tabTitle: t("view.newsletter.tabs.DSD"),
    //   content: content,
    //   totalPages: totalPages,
    //   currentPage: currentPage,
    //   handlePageChange: handlePageChange,
    // },
    // {
    //   id: 2,
    //   tabTitle: t("view.newsletter.tabs.Dgum"),
    //   content: content,
    //   totalPages: totalPages,
    //   currentPage: currentPage,
    //   handlePageChange: handlePageChange,
    // },
  ];

  // LIFE CYCLE
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        let allData = [];
        let currentPageIndex = 1;
        let totalPageCount = 1;

        while (currentPageIndex <= totalPageCount) {
          const response = await newsAndMediaService.getBrochuresByDir(
            selectedTab === 0 ? "DGPC" : selectedTab === 1 ? "DSD" : "DGUM",
            currentPageIndex,
            pageSize
          );

          if (!response?.data) break;

          allData = [...allData, ...response.data];
          totalPageCount = response?.meta?.pagination?.pageCount;
          currentPageIndex++;
        }

        const sortedData = allData.sort((a, b) => {
          const dateA = new Date(a.attributes.date);
          const dateB = new Date(b.attributes.date);
          return isNaN(dateA.getTime())
            ? 1
            : isNaN(dateB.getTime())
            ? -1
            : dateB.getTime() - dateA.getTime();
        });

        setBrochures({ data: sortedData });
        setTotalPages(Math.ceil(sortedData.length / pageSize));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchAllData();
  }, [selectedTab]);

  // EVENT HANDLERS
  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
  };

  return (
    <LegislationDetailContainer ref={topRef}>
      <TitleWrapper>
        <BackToGuidesWrapper
          biggerCursor={biggerCursor}
          onClick={() => navigate(appRoutes.news)}
        >
          <BackToGuidesImage src={LeftArrow} alt="backIcon" />

          <BackToGuidesButton>
            <CustomText
              text={t("view.newsletter.button")}
              type={TextType.H5boldspaced}
              color={COLORS.RaisinBlack}
            />
          </BackToGuidesButton>
        </BackToGuidesWrapper>
        <CustomText type={TextType.H2} text={t("view.newsletter.title")} />
      </TitleWrapper>
      <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} />
      {tabs[selectedTab].content.props.children?.length > 0 ? (
        tabs[selectedTab].content
      ) : (
        <NoDataContainer>
          <SvgContainer>
            <NoDataDark />
          </SvgContainer>
          <CustomText
            text={t("app.noData")}
            type={TextType.P1}
            color={COLORS.MetalicSilver}
          />
        </NoDataContainer>
      )}
      {brochures?.data?.length > 0 && totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      )}
    </LegislationDetailContainer>
  );
};

export default Newsletter;
